import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, Users, Calendar, BarChart3, Shield, Zap, 
  ChevronDown, ChevronUp, Menu, X, Star, Clock, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_hoop-tracker-7/artifacts/tisywt9m_attendance%20tracker%20logo.png';

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const features = [
    {
      icon: <Users className="h-8 w-8 text-blue-600" />,
      title: 'Athlete Management',
      description: 'Easily manage your roster with detailed athlete profiles, contact information, and program assignments.'
    },
    {
      icon: <Calendar className="h-8 w-8 text-green-600" />,
      title: 'Program Scheduling',
      description: 'Create and manage multiple training programs with flexible scheduling options.'
    },
    {
      icon: <CheckCircle className="h-8 w-8 text-purple-600" />,
      title: 'Attendance Tracking',
      description: 'Track attendance with one click. Mark athletes as present, absent, late, or excused.'
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-orange-600" />,
      title: 'Reports & Analytics',
      description: 'Generate detailed reports and export data in PDF, Excel, or CSV formats.'
    },
    {
      icon: <Shield className="h-8 w-8 text-red-600" />,
      title: 'Team Collaboration',
      description: 'Invite coaches and assistants to help manage your programs with role-based access.'
    },
    {
      icon: <Clock className="h-8 w-8 text-teal-600" />,
      title: 'Attendance History',
      description: 'Access complete attendance history and track athlete participation over time.'
    }
  ];

  const pricingPlans = [
    {
      name: 'Free',
      price: '$0',
      period: 'forever',
      description: 'Perfect for small teams getting started',
      features: [
        { text: 'Up to 3 programs', included: true },
        { text: 'Up to 30 athletes', included: true },
        { text: 'Basic attendance tracking', included: true },
        { text: '30-day attendance history', included: true },
        { text: 'Team collaboration', included: false },
        { text: 'CSV/PDF/Excel exports', included: false },
        { text: 'Unlimited history', included: false }
      ],
      buttonText: 'Get Started Free',
      buttonLink: '/register',
      highlighted: false
    },
    {
      name: 'Pro',
      price: '$29',
      period: '/year',
      yearlyPrice: null,
      description: 'For growing organizations that need more',
      features: [
        { text: 'Up to 20 programs', included: true },
        { text: 'Up to 200 athletes', included: true },
        { text: 'Team collaboration (up to 5)', included: true },
        { text: 'Unlimited attendance history', included: true },
        { text: 'CSV, PDF & Excel exports', included: true },
        { text: 'Full analytics dashboard', included: true }
      ],
      buttonText: 'Get Pro Now',
      buttonLink: '/register',
      highlighted: true
    },
    {
      name: 'Enterprise',
      price: '$99',
      period: '/year',
      description: 'White-label branding & API access',
      features: [
        { text: 'Unlimited programs', included: true },
        { text: 'Up to 1,000 athletes', included: true },
        { text: 'Team collaboration (up to 25)', included: true },
        { text: 'White-label branding', included: true },
        { text: 'REST API access', included: true },
        { text: 'Custom brand colors & logo', included: true },
        { text: 'Remove "Powered by" footer', included: true }
      ],
      buttonText: 'Get Enterprise',
      buttonLink: '/register',
      highlighted: false
    }
  ];

  const faqs = [
    {
      question: 'How do I get started with Attendance Tracker?',
      answer: 'Simply create a free account, add your programs and athletes, and start tracking attendance right away. No credit card required for the free plan.'
    },
    {
      question: 'Can I upgrade or downgrade my plan at any time?',
      answer: 'Yes! You can upgrade to Pro at any time to unlock unlimited features. You can also downgrade back to Free, though you may need to reduce your programs/athletes to fit within the free limits.'
    },
    {
      question: 'How many team members can I invite?',
      answer: 'Pro users can invite unlimited team members including managers and assistant coaches. Each team member gets their own login with role-based permissions.'
    },
    {
      question: 'What export formats are supported?',
      answer: 'Free users can export to CSV format. Pro users additionally get PDF and Excel (XLSX) export options with professional formatting.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Yes, we take security seriously. All data is encrypted, and each organization\'s data is completely isolated. We never share your information with third parties.'
    },
    {
      question: 'Can I import existing athlete data?',
      answer: 'Yes! You can import athletes from a CSV file. We provide a template to make the import process easy.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept PayPal for Pro subscriptions. The Pro plan is $29 per year - less than $2.50 per month!'
    }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <img src={LOGO_URL} alt="Attendance Tracker" className="h-10 w-auto" />
              <span className="font-bold text-xl text-gray-900 hidden sm:block">Attendance Tracker</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => scrollToSection('features')} 
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                Features
              </button>
              <button 
                onClick={() => scrollToSection('pricing')} 
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                Pricing
              </button>
              <button 
                onClick={() => scrollToSection('faq')} 
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                FAQ
              </button>
              <Link to="/login">
                <Button variant="ghost">Login</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  Create Free Account
                </Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100">
            <div className="px-4 py-4 space-y-3">
              <button 
                onClick={() => scrollToSection('features')} 
                className="block w-full text-left py-2 text-gray-600"
              >
                Features
              </button>
              <button 
                onClick={() => scrollToSection('pricing')} 
                className="block w-full text-left py-2 text-gray-600"
              >
                Pricing
              </button>
              <button 
                onClick={() => scrollToSection('faq')} 
                className="block w-full text-left py-2 text-gray-600"
              >
                FAQ
              </button>
              <div className="pt-3 border-t border-gray-100 space-y-2">
                <Link to="/login" className="block">
                  <Button variant="outline" className="w-full">Login</Button>
                </Link>
                <Link to="/register" className="block">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700">
                    Create Free Account
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-100">
              <Star className="h-3 w-3 mr-1" /> Trusted by coaches everywhere
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Track Athlete Attendance
              <span className="text-blue-600"> Effortlessly</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
              The simple, powerful way for coaches to manage programs, track attendance, 
              and keep their teams organized. Start free, upgrade when you need more.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-lg px-8 py-6">
                  Get Started Free
                </Button>
              </Link>
              <button 
                onClick={() => scrollToSection('features')}
                className="text-gray-600 hover:text-gray-900 flex items-center"
              >
                Learn more <ChevronDown className="ml-1 h-4 w-4" />
              </button>
            </div>
            <p className="mt-4 text-sm text-gray-500">
              No credit card required • Free forever plan available
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Everything you need to manage your team
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Powerful features designed specifically for coaches and sports organizations.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Simple, transparent pricing
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Start free and upgrade as your team grows. No hidden fees.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <Card 
                key={index} 
                className={`relative ${plan.highlighted ? 'border-2 border-blue-600 shadow-xl' : 'border border-gray-200'}`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-blue-600 text-white px-4 py-1">
                      Most Popular
                    </Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-500">{plan.period}</span>
                  </div>
                  {plan.yearlyPrice && (
                    <p className="text-sm text-green-600 mt-1">
                      or {plan.yearlyPrice} (save 50%)
                    </p>
                  )}
                  <p className="text-gray-600 mt-2">{plan.description}</p>
                </CardHeader>
                <CardContent className="pt-4">
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center">
                        <CheckCircle 
                          className={`h-5 w-5 mr-3 ${feature.included ? 'text-green-500' : 'text-gray-300'}`} 
                        />
                        <span className={feature.included ? 'text-gray-700' : 'text-gray-400'}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Link to={plan.buttonLink}>
                    <Button 
                      className={`w-full ${plan.highlighted ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                      variant={plan.highlighted ? 'default' : 'outline'}
                    >
                      {plan.buttonText}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Frequently Asked Questions
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Got questions? We've got answers.
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  className="w-full px-6 py-4 text-left flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-medium text-gray-900">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Ready to streamline your attendance tracking?
          </h2>
          <p className="mt-4 text-lg text-blue-100 max-w-2xl mx-auto">
            Join coaches who are saving time and staying organized with Attendance Tracker.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8">
                Create Free Account
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8">
                Login
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <img src={LOGO_URL} alt="Attendance Tracker" className="h-10 w-auto" />
                <span className="font-bold text-xl text-white">Attendance Tracker</span>
              </div>
              <p className="text-gray-400 max-w-md">
                The simple, powerful attendance tracking solution for coaches and sports organizations.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('features')} className="hover:text-white transition-colors">Features</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="hover:text-white transition-colors">Pricing</button></li>
                <li><button onClick={() => scrollToSection('faq')} className="hover:text-white transition-colors">FAQ</button></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} Attendance Tracker App. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
